import { isPending } from "App/apiMiddleware";
import { getContentMasterNodes, getContentWithoutDeleted } from "Areas/Documents/content/redux/selectors";
import { getInnerObjectsSelectedIds } from "Areas/Documents/documentDetails/redux/selectors";
import { getEdgesSource, getNodesSource } from "Areas/Documents/graph/redux/selectors";
import { getDeleted, getGeometry, getPoints } from "Areas/Documents/map/redux/selectors";
import { getTableNavigationRows, getNodesSource as getTableNodesSource } from "Areas/Documents/table/redux/selectors";
import { getReloadOntologyInProgress } from "Areas/Settings/connections/redux/selectors";
import { getSaveInProgress as getOntologicalModelSaveInProgress } from "Areas/Settings/ontologicalModel/redux/selectors";
import { DOCUMENT_TYPE } from "Common/dictionaries/documentType";
import compact from "lodash/compact";
import filter from "lodash/filter";
import find from "lodash/find";
import forEach from "lodash/forEach";
import get from "lodash/get";
import includes from "lodash/includes";
import intersection from "lodash/intersection";
import isEmpty from "lodash/isEmpty";
import keyBy from "lodash/keyBy";
import map from "lodash/map";
import pick from "lodash/pick";
import reduce from "lodash/reduce";
import startsWith from "lodash/startsWith";
import values from "lodash/values";
import { createSelector } from "reselect";
import { CONTENT_MODE_TAB, NAMESPACE, PAGE_NAMESPACE, TAB } from "../constants";
import actionTypes from "./actionTypes";
import { getActiveDocument, getActiveDocumentId, getActiveDocumentType, getDocuments, getSelectedDocumentIds } from "./getActiveDocumentId";
import getAllDocuments from "./getAllDocuments";
import getProjectId from "./getProjectId";
import keys from "lodash/keys";
export { getActiveDocument, getActiveDocumentId, getActiveDocumentType, getAllDocuments, getDocuments, getProjectId, getSelectedDocumentIds };
export const getIsExpanded = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].isExpanded`), (isExpanded) => Boolean(isExpanded));
export const getTab = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].tab`), (tab) => tab || TAB.NAVIGATION);
export const getContentModeTab = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].contentModeTab`), (contentModeTab) => contentModeTab || CONTENT_MODE_TAB.FOUND);
export const getNavigateToId = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].navigateToId`), (navigateToId) => navigateToId);
export const getObjectsOutdated = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].objectsOutdated`), (objectsOutdated) => Boolean(objectsOutdated));
export const getObjectsUsedByUI = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].objectsUsedByUI`), (objectsUsedByUI) => Boolean(objectsUsedByUI));
export const getObjectsIgnoreOutdated = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].objects`), (objects) => objects);
export const getObjects = createSelector((state) => getObjectsOutdated(state), (state) => getObjectsIgnoreOutdated(state), (objectsOutdated, objects) => !objectsOutdated ? objects : undefined);
export const getAvailableMethodsMap = createSelector((state) => getObjects(state), (objects) => objects
    ? reduce(objects, (res, curr) => ({
        ...res,
        [curr.Id]: curr.AvailableMethods
    }), {})
    : undefined);
export const getMethods = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].methods`), (methods) => methods);
export const getSelectedMethods = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].selectedMethods`, {}), (state) => getMethods(state), (selected, methods) => {
    if (!selected || !methods)
        return [];
    return compact(map(selected, (nodesSource, methodId) => {
        const method = find(methods, ["Id", methodId]);
        return method
            ? {
                method,
                nodesSource
            }
            : undefined;
    }));
});
export const getNavigationObjects = createSelector((state) => getActiveDocumentType(state), (state) => getNodesSource(state), (state) => getEdgesSource(state), (state) => getTableNavigationRows(state), (state) => getTableNodesSource(state), (state) => getPoints(state), (state) => getGeometry(state), (state) => getDeleted(state), (state) => getContentWithoutDeleted(state), (state) => getContentMasterNodes(state), (selectedType, nodesSource, edgesSource, tableNavigationRows, tableNodeSource, points, geometry, deletedMapObjectIds, content, contentMasterNodes) => {
    if (selectedType === DOCUMENT_TYPE.GRAPH) {
        if (!nodesSource || !edgesSource)
            return undefined;
        return { nodesSource };
    }
    if (selectedType === DOCUMENT_TYPE.TABLE)
        return tableNavigationRows !== undefined
            ? { tableRow: tableNavigationRows, nodesSource: tableNodeSource }
            : undefined;
    if (selectedType === DOCUMENT_TYPE.MAP) {
        if (!points && !geometry)
            return undefined;
        const resultNodesSource = {};
        const mapObject = {};
        forEach(points, p => {
            if (!p.NodeId || includes(deletedMapObjectIds, p.NodeId))
                return;
            mapObject[p.NodeId] = p;
            if (p.NodeSource)
                resultNodesSource[p.NodeId] = p.NodeSource;
        });
        forEach(geometry, g => {
            if (!g.Id || includes(deletedMapObjectIds, g.Id))
                return;
            mapObject[g.Id] = g;
        });
        return { nodesSource: resultNodesSource, mapObject };
    }
    if (selectedType === DOCUMENT_TYPE.CONTENT) {
        return content !== undefined || contentMasterNodes !== undefined
            ? { content: keyBy(content, "Id"), contentMasterNodes }
            : undefined;
    }
    return undefined;
});
export const getFilteredNavigationObjects = createSelector((state) => getActiveDocumentType(state), (state) => getNodesSource(state), (state) => getEdgesSource(state), (state) => getTableNavigationRows(state), (state) => getTableNodesSource(state), (state) => getPoints(state), (state) => getGeometry(state), (state) => getDeleted(state), (state) => getContentWithoutDeleted(state), (state) => getContentMasterNodes(state), (selectedType, nodesSource, edgesSource, tableNavigationRows, tableNodeSource, points, geometry, deletedMapObjectIds, content, contentMasterNodes) => {
    if (selectedType === DOCUMENT_TYPE.GRAPH) {
        if (!nodesSource || !edgesSource)
            return undefined;
        const relationCount = {};
        forEach(keys(nodesSource), nodeId => {
            relationCount[nodeId] = 0;
        });
        forEach(values(edgesSource), edge => {
            if (nodesSource[edge.From])
                relationCount[edge.From]++;
            if (nodesSource[edge.To])
                relationCount[edge.To]++;
        });
        let minRelations = 0;
        const edgesArray = values(edgesSource);
        if (edgesArray.length > 0) {
            const allRelations = values(relationCount);
            const totalRelations = allRelations.reduce((sum, count) => sum + count, 0);
            const averageRelations = totalRelations / allRelations.length;
            minRelations = Math.round(averageRelations);
        }
        const filteredNodesSource = Object.fromEntries(filter(Object.entries(nodesSource), ([nodeId]) => relationCount[nodeId] >= minRelations));
        return { nodesSource: filteredNodesSource };
    }
    if (selectedType === DOCUMENT_TYPE.TABLE)
        return tableNavigationRows !== undefined
            ? { tableRow: tableNavigationRows, nodesSource: tableNodeSource }
            : undefined;
    if (selectedType === DOCUMENT_TYPE.MAP) {
        if (!points && !geometry)
            return undefined;
        const resultNodesSource = {};
        const mapObject = {};
        forEach(points, p => {
            if (!p.NodeId || includes(deletedMapObjectIds, p.NodeId))
                return;
            mapObject[p.NodeId] = p;
            if (p.NodeSource)
                resultNodesSource[p.NodeId] = p.NodeSource;
        });
        forEach(geometry, g => {
            if (!g.Id || includes(deletedMapObjectIds, g.Id))
                return;
            mapObject[g.Id] = g;
        });
        return { nodesSource: resultNodesSource, mapObject };
    }
    if (selectedType === DOCUMENT_TYPE.CONTENT) {
        return content !== undefined || contentMasterNodes !== undefined
            ? { content: keyBy(content, "Id"), contentMasterNodes }
            : undefined;
    }
    return undefined;
});
export const getScriptEditorPanelShow = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].scriptEditorPanel.show`, false), (show) => show);
export const getScriptEditorPanelData = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].scriptEditorPanel.data`), (data) => data);
export const getScriptEditorPanelTargetSelector = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].scriptEditorPanel.targetSelector`), (targetSelector) => targetSelector);
export const getScriptEditorPanelTargetOffset = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].scriptEditorPanel.targetOffset`), (targetOffset) => targetOffset);
export const getScriptEditorPanelChanges = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].scriptEditorPanel.draft`), (draft) => draft);
export const getScriptEditorPanelHasChanges = createSelector((state) => getScriptEditorPanelChanges(state), (draft) => !isEmpty(draft));
export const getScriptEditorPanelDraft = createSelector((state) => getScriptEditorPanelData(state), (state) => getScriptEditorPanelChanges(state), (state) => getActiveDocumentId(state), (data, draft, documentId) => {
    if (!documentId || !data)
        return undefined;
    return {
        ...data,
        ...(draft || {})
    };
});
export const getScripts = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].scripts`), (scripts) => scripts ? values(scripts) : undefined);
export const getMappingToTaskIds = createSelector((state, documentId) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].tasks.mappingToTaskIds[${documentId}]`), (mappingToTaskIds) => mappingToTaskIds);
export const getTasks = createSelector((state, documentId) => getMappingToTaskIds(state, documentId), (state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].tasks.data`, {}), (mappingToTaskIds, tasks) => mappingToTaskIds ? values(pick(tasks, mappingToTaskIds)) : undefined);
export const getOutdated = createSelector((state, documentId) => getMappingToTaskIds(state, documentId), (state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].tasks.outdated`, []), (mappingToTaskIds, outdated) => {
    const result = intersection(mappingToTaskIds, outdated);
    return result.length ? result : undefined;
});
export const getSourceTaskIdsToHighlight = createSelector((state) => get(state, `[${PAGE_NAMESPACE}][${NAMESPACE}].sourceTaskIdsToHighlight[${getActiveDocumentId(state)}]`), (sourceTaskIds) => sourceTaskIds);
export const getDocumentsInProgress = (state) => isPending(state, `${actionTypes.GET_DOCUMENTS}_${getProjectId(state)}`);
export const getObjectsInProgress = (state) => isPending(state, actionTypes.GET_OBJECTS) ||
    getOntologicalModelSaveInProgress(state) ||
    getReloadOntologyInProgress(state);
export const getTasksInProgress = (state, projectId) => isPending(state, `${actionTypes.GET_TASKS}_${projectId}`);
export const getScriptEditorPanelInProgress = (state) => { var _a; return isPending(state, `${actionTypes.SAVE_SCRIPT}_${(_a = getScriptEditorPanelData(state)) === null || _a === void 0 ? void 0 : _a.Id}`); };
export const getTabScriptsInProgress = (state) => isPending(state, actionTypes.GET_SCRIPTS) ||
    getObjectsInProgress(state) ||
    isPending(state, e => startsWith(e, `${actionTypes.RUN_SCRIPT}_${getActiveDocumentId(state)}`)) ||
    isPending(state, e => startsWith(e, `${actionTypes.DELETE_SCRIPT}_`));
export const getTabMethodsInProgress = (state) => isPending(state, actionTypes.GET_METHODS) ||
    getObjectsInProgress(state) ||
    isPending(state, `${actionTypes.RUN_METHODS}_${getActiveDocumentId(state)}`);
export const getSelectedNodesSource = createSelector((state) => { var _a; return (_a = getNavigationObjects(state)) === null || _a === void 0 ? void 0 : _a.nodesSource; }, (state) => getInnerObjectsSelectedIds(state), (nodesSource, selectedIds) => {
    if (isEmpty(nodesSource) || isEmpty(selectedIds))
        return [];
    return filter(nodesSource, e => includes(selectedIds, e.NodeId));
});
